import {
  type CSSProperties,
  type ForwardedRef,
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { customColors } from "tailwind.config";

import useAccessibleDropdown from "$/lib/hooks/useAccessibleDropdown";
import DropDownIcon from "$/lib/icons/DropDownIcon";
import { WarningIcon } from "$/lib/icons/WarningIcon";
import { cn } from "$/lib/utils/functions/misc.functions";

import type { DropdownProps } from "./Dropdown.types";
import DropdownOptionsList from "./DropdownOptionsList";

const PADDING = 10;

function DropdownInner<T>(
  {
    label,
    hideLabel,
    placeholder,
    options,
    selected,
    multi,
    isError,
    errorMessage,
    disabled,
    containerClassName,
    buttonClassName,
    overlayClassName,
    errorClassName,
    customIcon,
    onSelect,
  }: DropdownProps<T>,
  forwardedTriggerRef: ForwardedRef<HTMLButtonElement>,
) {
  const [focusedOptionIndex, setFocusedOptionIndex] = useState(-1);
  const [selectionLabel, setSelectionLabel] = useState("");

  const listId = useId();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);
  const optionsListRef = useRef<HTMLUListElement | null>(null);

  const { isExpanded, overlayProps, overlayPlacement, close, toggleExpanded } =
    useAccessibleDropdown({
      containerRef,
      triggerRef,
      overlayRef,
    });

  const overlayStyle = overlayProps.style as CSSProperties;

  const handleScrollToOption = useCallback((focusedIndex: number) => {
    if (focusedIndex === -1) return;

    const list = optionsListRef.current;
    if (!list) return;

    const focusedOption = list.childNodes[focusedIndex] as
      | HTMLElement
      | undefined;

    if (!focusedOption) return;

    const listRect = list.getBoundingClientRect();
    const optionRect = focusedOption.getBoundingClientRect();

    if (optionRect.bottom > listRect.bottom) {
      list.scrollTo({
        top: list.scrollTop + optionRect.bottom - listRect.bottom + PADDING,
      });
    }

    if (optionRect.top < listRect.top) {
      list.scrollTo({
        top: list.scrollTop - (listRect.top - optionRect.top) - PADDING,
      });
    }
  }, []);

  const handleSelect = useCallback(
    (option: T, index: number) => {
      onSelect(option, index);
      if (!multi) {
        close();
        optionsListRef.current?.scrollTo({ top: 0 });
      }
    },
    [multi, onSelect, close],
  );

  useEffect(() => {
    if (!multi) {
      const selectedOption = options.find(
        (option) => option.value === selected,
      );
      if (selectedOption) {
        setSelectionLabel(selectedOption.label);
      }
    }
  }, [multi, selected, options]);

  useEffect(() => {
    optionsListRef.current?.scrollTo({ top: 0 });

    setFocusedOptionIndex(-1);
  }, [isExpanded]);

  useEffect(() => {
    if (!isExpanded) return;

    const handleArrowsMovement = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        setFocusedOptionIndex((p) => {
          if (p === -1) return 0;
          const newFocusedOptionIndex = Math.min(p + 1, options.length - 1);
          handleScrollToOption(newFocusedOptionIndex);
          return newFocusedOptionIndex;
        });
      }

      if (e.key === "ArrowUp") {
        setFocusedOptionIndex((p) => {
          const newFocusedOptionIndex = Math.max(p - 1, 0);
          handleScrollToOption(newFocusedOptionIndex);
          return newFocusedOptionIndex;
        });
      }
    };

    const overlay = overlayRef.current;

    overlay?.addEventListener("keydown", handleArrowsMovement);

    return () => {
      overlay?.removeEventListener("keydown", handleArrowsMovement);
    };
  }, [isExpanded, options.length, handleScrollToOption]);

  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyboardSelection = (e: KeyboardEvent) => {
      if (e.key === "Enter" && focusedOptionIndex !== -1) {
        e.preventDefault();
        e.stopPropagation();
        const focusedOption = options.at(focusedOptionIndex);
        if (focusedOption) {
          handleSelect(focusedOption.value, focusedOptionIndex);
          if (!multi) close();
        }
      }
    };

    const overlay = overlayRef.current;

    overlay?.addEventListener("keydown", handleKeyboardSelection);

    return () => {
      overlay?.removeEventListener("keydown", handleKeyboardSelection);
    };
  }, [focusedOptionIndex, isExpanded, multi, options, handleSelect, close]);

  const placeholderText = multi ? placeholder : selectionLabel || placeholder;

  const setTriggerRef = (el: HTMLButtonElement) => {
    triggerRef.current = el;
    if (forwardedTriggerRef) {
      if (typeof forwardedTriggerRef === "function") {
        forwardedTriggerRef(el);
      } else {
        forwardedTriggerRef.current = el;
      }
    }
  };

  return (
    <div
      ref={containerRef}
      className={cn("group relative flex w-full flex-col", containerClassName)}
    >
      {!!label && !hideLabel && (
        <label className="mb-1 text-sm font-semibold">{label}</label>
      )}
      <button
        ref={setTriggerRef}
        type="button"
        role="combobox"
        disabled={disabled}
        aria-autocomplete="none"
        aria-expanded={isExpanded}
        aria-haspopup="listbox"
        aria-invalid={isError}
        aria-controls={listId}
        onClick={toggleExpanded}
        className={cn(
          "border-border inline-flex h-12 min-h-12 w-full items-center justify-between gap-4 rounded-lg border-2 bg-white px-4 py-2.5 text-sm font-medium leading-none text-black outline-none duration-150 group-focus-within:border-primary-500 data-[state='open']:border-primary-500",
          "z-30 disabled:cursor-not-allowed disabled:opacity-50",
          "aria-invalid:!border-error aria-invalid:group-focus-within:border-primary/30",
          buttonClassName,
        )}
      >
        <span
          className="inline-flex w-fit items-center gap-2"
          title={placeholderText}
        >
          <span
            className={cn(
              "line-clamp-1 text-ellipsis text-left text-textInfo",
              { "text-black": selectionLabel },
            )}
          >
            {placeholderText}
          </span>
          {multi && (
            <span
              className={cn(
                "text-snow size-5 rounded-circle bg-primary p-0.5 text-xs duration-100",
                !selected?.length && "opacity-0",
              )}
            >
              {selected?.length || 1}
            </span>
          )}
        </span>
        {customIcon ?? <DropDownIcon className="size-5" />}
      </button>
      <div
        tabIndex={-1}
        aria-expanded={isExpanded}
        data-placement={overlayPlacement}
        className={cn(
          "pointer-events-none absolute w-full min-w-20 -translate-y-2 overflow-hidden rounded-md bg-white px-0 py-4 pb-0 opacity-0 duration-150 aria-expanded:pointer-events-auto data-[placement='top']:translate-y-2 data-[placement='top']:aria-expanded:translate-y-0",
          "w-11/12 border border-border aria-expanded:-translate-y-4 aria-expanded:opacity-100",
          overlayClassName,
        )}
        ref={overlayRef}
        {...overlayProps}
        style={overlayStyle}
      >
        <DropdownOptionsList
          id={listId}
          ref={optionsListRef}
          options={options}
          focusedOptionIndex={focusedOptionIndex}
          handleSetFocusedOptionIndex={(i) => setFocusedOptionIndex(i)}
          handleSelect={handleSelect}
          multi={multi ?? false}
          selected={selected}
        />
      </div>
      {isError && errorMessage && (
        <p
          className={cn(
            "mt-1 flex items-center gap-1 text-xs text-error",
            errorClassName,
          )}
        >
          <WarningIcon color={customColors.error.DEFAULT} className="size-4" />
          {errorMessage}
        </p>
      )}
    </div>
  );
}

const Dropdown = forwardRef(DropdownInner) as {
  <T>(
    props: DropdownProps<T> & { ref?: ForwardedRef<HTMLButtonElement> },
  ): ReactNode;
  displayName: string;
};
Dropdown.displayName = "Dropdown";

export default Dropdown;
